// Pure helpers for "Correct this reply": which bubbles get the button (or
// which reply a "To review" link opens), what label each outgoing bubble
// carries, how a message is quoted in a lesson, and how the turn snapshot of a
// reply is read for the panel.
import {
  AiTurnDetail,
  AiTurnLiveState,
  AiTurnMessage,
  AiTurnTraceRound,
  AiTurnTraceTool,
  BaseMessage,
  ChatMessage,
  WhatsAppMessage,
  WhatsAppMessageCard
} from "../aiConversations.types";
import { WhatsAppMessageOrigin, WhatsAppMessageType } from "../constants/enums";

const CORRECTABLE_VARIANTS = new Set<ChatMessage["variant"]>([
  "text",
  "image",
  "carousel",
  "video"
]);

// The fields a WhatsApp row adds to its chat message. A backend that does not
// send inResponseToMessageId yet leaves turnMessageId null, so no bubble can
// be corrected; origin stays undefined until the backend sends it.
export const toTurnFields = (
  message: Pick<WhatsAppMessage, "inResponseToMessageId" | "origin">
): Pick<BaseMessage, "turnMessageId" | "origin"> => ({
  turnMessageId: message.inResponseToMessageId ?? null,
  origin: message.origin
});

// Only a reply the AI wrote for a customer message can be corrected: not a
// team member's message, not a fixed sentence the code sends ("system").
export const isCorrectableMessage = (message: ChatMessage): boolean =>
  Boolean(message.isAI) &&
  message.direction === "outgoing" &&
  Boolean(message.turnMessageId) &&
  message.origin !== WhatsAppMessageOrigin.System &&
  CORRECTABLE_VARIANTS.has(message.variant);

// The reply the "To review" page links to (`?turnMessageId=`), as the chat
// message the correction panel opens on: the turn's first AI reply that is not
// a fixed sentence and has words or products. Null when the turn has none, or
// when that reply cannot be corrected.
export const chatMessageFromTurn = (
  turn: Pick<AiTurnDetail, "turnMessageId" | "replies">,
  formatTime: (iso: string) => string
): ChatMessage | null => {
  const reply = turn.replies.find(
    (m) =>
      m.sender === "ai" &&
      m.origin !== WhatsAppMessageOrigin.System &&
      (Boolean(m.text?.trim()) || (m.cards?.length ?? 0) > 0)
  );
  if (!reply) return null;

  const base = {
    id: reply.id,
    direction: "outgoing" as const,
    isAI: true,
    turnMessageId: turn.turnMessageId,
    origin: reply.origin,
    time: formatTime(reply.createdAt)
  };
  const message: ChatMessage = reply.cards?.length
    ? {
        ...base,
        variant: "carousel",
        cards: reply.cards,
        text: reply.text ?? undefined
      }
    : { ...base, variant: "text", text: reply.text ?? "" };

  return isCorrectableMessage(message) ? message : null;
};

export type OutgoingSenderLabel = "ai" | "system" | "human";

// The label under an outgoing bubble (`aiTurn.label.<label>`); null for the
// customer's messages.
export const outgoingSenderLabel = (
  message: Pick<BaseMessage, "direction" | "isAI" | "origin">
): OutgoingSenderLabel | null => {
  if (message.direction !== "outgoing") return null;
  if (message.origin === WhatsAppMessageOrigin.System) return "system";
  return message.isAI ? "ai" : "human";
};

// An AI row written before origins were recorded. Undefined (a channel without
// origins) is not "unknown".
export const isUnknownOriginMessage = (
  message: Pick<BaseMessage, "direction" | "isAI" | "origin">
): boolean =>
  message.direction === "outgoing" &&
  Boolean(message.isAI) &&
  message.origin === null;

const cardNames = (cards: WhatsAppMessageCard[] | null | undefined) =>
  (cards ?? [])
    .map((card) => card.name.trim())
    .filter(Boolean)
    .join(", ");

// The reply quoted as "X" in the lesson: its text, and the names of the
// products it showed.
export const toBadReplyText = (message: ChatMessage): string => {
  switch (message.variant) {
    case "text":
      return message.text.trim();
    case "image":
    case "video":
      return message.text?.trim() ?? "";
    case "carousel":
      return [message.text?.trim(), cardNames(message.cards)]
        .filter(Boolean)
        .join("\n");
    default:
      return "";
  }
};

// One row of the turn context, as plain text.
export const turnMessageText = (message: AiTurnMessage): string => {
  const parts = [message.text?.trim(), cardNames(message.cards)].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join("\n") : `[${message.messageType}]`;
};

export const turnSenderLabelKey = (sender: AiTurnMessage["sender"]): string => {
  switch (sender) {
    case "customer":
      return "aiTurn.sender.customer";
    case "ai":
      return "aiTurn.label.ai";
    case "admin":
      return "aiTurn.label.human";
  }
};

export interface TurnStateSummary {
  cartItemCount: number;
  openOrders: number;
  awaitingPayment: number;
}

// The few numbers of the live state the panel shows; null when the state of
// that moment was not recorded.
export const summariseTurnLiveState = (
  liveState: AiTurnLiveState | null | undefined
): TurnStateSummary | null => {
  if (!liveState) return null;
  const orders = liveState.openOrders ?? [];
  const awaitingPayment = orders.filter((o) => o.isAwaitingPayment).length;
  return {
    cartItemCount: liveState.cart?.itemCount ?? 0,
    openOrders: orders.length - awaitingPayment,
    awaitingPayment
  };
};

// --- Turn snapshot (M1): what was recorded while the assistant answered. ---
// The backend already cleans the snapshot; these stay defensive so a malformed
// field hides one line instead of breaking the panel.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const finiteOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const collapseSpaces = (text: string) => text.replace(/\s+/g, " ").trim();

// The sentence next to the fidelity badge. A partial turn says which half of
// the snapshot is missing.
export const turnFidelityNoticeKey = (
  turn: Pick<AiTurnDetail, "fidelity" | "snapshot">
): string => {
  if (turn.fidelity === "partial") {
    if (turn.snapshot?.contextCaptured === false) {
      return "aiTurn.fidelity.partialNoContext";
    }
    if (turn.snapshot?.workflowCaptured === false) {
      return "aiTurn.fidelity.partialNoWorkflow";
    }
    return "aiTurn.fidelity.partial";
  }
  return turn.fidelity === "full"
    ? "aiTurn.fidelity.full"
    : "aiTurn.fidelity.reconstructed";
};

// Before it stores a reply, the backend rewrites the model's Markdown into
// WhatsApp formatting (backend `toWhatsAppText`): **bold** becomes *bold*,
// [label](url) becomes "label: url", "## Title" becomes *Title* and "* item"
// becomes "- item". So the stored text differs from the raw one even when
// nothing was cleaned. These marks are left out of the comparison, on both
// sides, so a reply whose format alone changed is not shown as cleaned.
const comparableReplyText = (text: string) =>
  collapseSpaces(
    text
      .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, "$1: $2")
      .replace(/^[ \t]*[-*+][ \t]+/gm, "")
      .replace(/[*_~`#]/g, "")
  );

// The model's last text before the automatic cleaning, only when the customer
// got something else: a cleaned or cut reply, or no text at all. Compared with
// the reply texts the model wrote (not the fixed sentences or tool captions),
// trimmed, with whitespace collapsed and the WhatsApp formatting left out.
export const turnRawReplyToShow = (
  turn: Pick<AiTurnDetail, "snapshot" | "replies">
): string | null => {
  const raw = turn.snapshot?.rawReplyText;
  if (typeof raw !== "string" || !collapseSpaces(raw)) return null;

  const sent = turn.replies
    .filter(
      (m) =>
        m.messageType === WhatsAppMessageType.Text &&
        m.origin !== WhatsAppMessageOrigin.System &&
        m.origin !== WhatsAppMessageOrigin.Tool &&
        Boolean(m.text)
    )
    .map((m) => m.text)
    .join("\n");

  return comparableReplyText(raw) === comparableReplyText(sent) ? null : raw;
};

const toTraceTool = (tool: Record<string, unknown>): AiTurnTraceTool => ({
  name: String(tool.name),
  input: typeof tool.input === "string" ? tool.input : "",
  resultPreview:
    typeof tool.resultPreview === "string" ? tool.resultPreview : "",
  isError: tool.isError === true
});

// The recorded steps of the reply. Keeps the items with a numeric `round` and
// a `tools` array, and the tools that have a name.
export const turnTraceRounds = (
  turn: Pick<AiTurnDetail, "snapshot">
): AiTurnTraceRound[] => {
  const trace: unknown = turn.snapshot?.trace;
  if (!Array.isArray(trace)) return [];

  return trace
    .filter(isRecord)
    .filter((r) => finiteOrNull(r.round) !== null && Array.isArray(r.tools))
    .map((r) => {
      const forced = isRecord(r.forced) ? r.forced : null;
      return {
        round: r.round as number,
        stopReason: typeof r.stopReason === "string" ? r.stopReason : null,
        forced:
          forced && typeof forced.reason === "string"
            ? {
                reason: forced.reason,
                toolName:
                  typeof forced.toolName === "string" ? forced.toolName : null
              }
            : null,
        draftText:
          typeof r.draftText === "string" && r.draftText.trim()
            ? r.draftText
            : null,
        tools: (r.tools as unknown[])
          .filter(isRecord)
          .filter((tool) => typeof tool.name === "string")
          .map(toTraceTool)
      };
    });
};

const failedCount = (list: unknown) =>
  Array.isArray(list)
    ? list.filter((entry) => typeof entry === "string" && !entry.trim()).length
    : 0;

// How many voice notes could not be transcribed and photos could not be read:
// the "" entries of the snapshot lists.
export const turnMediaFailures = (
  turn: Pick<AiTurnDetail, "snapshot">
): { voice: number; photo: number } => ({
  voice: failedCount(turn.snapshot?.transcripts),
  photo: failedCount(turn.snapshot?.imageDescriptions)
});

export interface TurnMediaText {
  kind: "voice" | "photo";
  text: string;
}

// Pairs the turn's voice notes and photos with what the assistant read from
// them, by message id. The snapshot lists follow the order the customer sent
// them in, so the i-th voice row gets the i-th transcript; this is done only
// when the counts are equal, because the inbound rows only approximate the
// burst the workflow received. Entries that failed ("") are not paired. When
// nothing can be paired, the "what the assistant read" field still has it all.
export const turnInboundMediaTexts = (
  turn: Pick<AiTurnDetail, "inbound" | "snapshot">
): Record<string, TurnMediaText> => {
  const paired: Record<string, TurnMediaText> = {};

  const pair = (
    messageType: WhatsAppMessageType,
    texts: unknown,
    kind: TurnMediaText["kind"]
  ) => {
    const rows = turn.inbound.filter(
      (m) => m.sender === "customer" && m.messageType === messageType
    );
    if (!Array.isArray(texts) || rows.length === 0) return;
    if (rows.length !== texts.length) return;

    rows.forEach((row, i) => {
      const text: unknown = texts[i];
      if (typeof text === "string" && text.trim()) {
        paired[row.id] = { kind, text: text.trim() };
      }
    });
  };

  pair(WhatsAppMessageType.Voice, turn.snapshot?.transcripts, "voice");
  pair(WhatsAppMessageType.Image, turn.snapshot?.imageDescriptions, "photo");
  return paired;
};

// The transcripts of the turn's voice notes, by message id, for the chat's
// voice bubbles.
export const turnVoiceTranscripts = (
  turn: Pick<AiTurnDetail, "inbound" | "snapshot">
): Record<string, string> => {
  const transcripts: Record<string, string> = {};
  Object.entries(turnInboundMediaTexts(turn)).forEach(([id, media]) => {
    if (media.kind === "voice") transcripts[id] = media.text;
  });
  return transcripts;
};

export const formatFcfa = (amount: number): string =>
  `${Math.round(amount).toLocaleString("en-US")} FCFA`;

export interface TurnCartLine {
  label: string;
  quantity: number;
  lineTotal: number | null;
}

export interface TurnOpenOrder {
  status: string;
  total: number;
  deliveryDate: string | null;
  isAwaitingPayment: boolean;
}

export interface TurnLiveStateDetails {
  cartLines: TurnCartLine[];
  subtotal: number | null;
  // Null when the live state has no address list (only counted, never shown).
  addressCount: number | null;
  // Null when an order lacks its status or total: the panel then shows the
  // counts of summariseTurnLiveState instead.
  orders: TurnOpenOrder[] | null;
  // The ISO dates of the delivery days the assistant was given, in order; []
  // when it was given none (or the turn predates them, 23 Oct 2026).
  deliveryDays: string[];
}

// The detail of the live state of that moment: the cart lines, the open
// orders, the delivery days offered and how many saved addresses. Null when it
// was not recorded.
export const turnLiveStateDetails = (
  liveState: AiTurnLiveState | null | undefined
): TurnLiveStateDetails | null => {
  if (!liveState) return null;

  const lines: unknown = liveState.cart?.lines;
  const rawLines: unknown[] = Array.isArray(lines) ? lines : [];
  const cartLines = rawLines
    .filter(isRecord)
    .filter((line) => typeof line.label === "string" && line.label.trim())
    .map((line) => ({
      label: String(line.label).trim(),
      quantity: finiteOrNull(line.quantity) ?? 1,
      lineTotal: finiteOrNull(line.lineTotal)
    }));

  const openOrders: unknown = liveState.openOrders;
  const rawOrders: unknown[] = Array.isArray(openOrders) ? openOrders : [];
  const orders = rawOrders.filter(isRecord).map((order) => ({
    status: typeof order.status === "string" ? order.status : null,
    total: finiteOrNull(order.total),
    deliveryDate:
      typeof order.deliveryDate === "string" ? order.deliveryDate : null,
    isAwaitingPayment: order.isAwaitingPayment === true
  }));
  const ordersComplete =
    orders.length === rawOrders.length &&
    orders.every((order) => order.status !== null && order.total !== null);

  const days: unknown = liveState.deliveryDays;
  const deliveryDays = (Array.isArray(days) ? days : [])
    .filter(isRecord)
    .map((day) => day.iso)
    .filter((iso): iso is string => typeof iso === "string" && iso.length > 0);

  return {
    cartLines,
    subtotal:
      cartLines.length > 0 ? finiteOrNull(liveState.cart?.subtotal) : null,
    addressCount: Array.isArray(liveState.addresses)
      ? liveState.addresses.length
      : null,
    orders: ordersComplete ? (orders as TurnOpenOrder[]) : null,
    deliveryDays
  };
};

// The code the backend stores in ai_turn_metric for an escalation: the
// model's own words are kept in the snapshot only.
export const ESCALATED_REASON_CODE = "escalate_to_human";

// The fixed reason prefixes the workflow writes, explained in plain words.
const OUTCOME_REASON_KEYS: { prefix: string; key: string }[] = [
  { prefix: "ai-exhausted:", key: "aiTurn.reason.exhausted" },
  { prefix: "ai-error:", key: "aiTurn.reason.error" },
  { prefix: "ai-silent:", key: "aiTurn.reason.silent" },
  { prefix: "ai-annotation-only:", key: "aiTurn.reason.annotationOnly" },
  { prefix: "ai-dropped-tool-calls:", key: "aiTurn.reason.droppedToolCalls" }
];

// Why the turn ended as it did: a plain sentence when the reason is a known
// code (`key`), and the recorded text (`detail`, null for the bare escalation
// code). Null when no reason was recorded (a normal reply).
export const turnOutcomeReason = (
  reason: string | null | undefined
): { key: string | null; detail: string | null } | null => {
  const text = reason?.trim();
  if (!text) return null;
  if (text === ESCALATED_REASON_CODE) {
    return { key: "aiTurn.reason.escalateToHuman", detail: null };
  }
  const known = OUTCOME_REASON_KEYS.find(({ prefix }) =>
    text.startsWith(prefix)
  );
  return { key: known?.key ?? null, detail: text };
};

// A tool input is JSON cut to a limit: indented when it still parses, as
// recorded otherwise.
export const prettyToolInput = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return input;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return input;
  }
};
